import { X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button.js";
import { cn } from "./cn.js";

type ToastTone = "success" | "error" | "info";
type ToastOptions = { title: string; description?: string; tone?: ToastTone };
type ToastItem = {
  id: number;
  title: string;
  description?: string | undefined;
  tone: ToastTone;
  leaving: boolean;
};

type ToastContextValue = { toast: (opts: ToastOptions) => void };

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX = 4;
const DURATION = 4000;
const EXIT_MS = 180;

const STRIPE: Record<ToastTone, string> = {
  success: "bg-ready",
  error: "bg-live",
  info: "bg-accent",
};

/** Top-right toast stack. A new toast is added on top, 4 at most
 *  (the oldest drops off), it auto-closes after 4s (or manually with ×). The context
 *  value is memoized; portaled into body. Both entries (match-day + admin)
 *  wrap their root in this provider. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timers = useRef<Map<number, number>>(new Map());

  const remove = useCallback((id: number) => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, EXIT_MS);
  }, []);

  const toast = useCallback(
    (opts: ToastOptions) => {
      const id = ++idRef.current;
      const item: ToastItem = {
        id,
        title: opts.title,
        description: opts.description,
        tone: opts.tone ?? "info",
        leaving: false,
      };
      setToasts((list) => [item, ...list].slice(0, MAX));
      timers.current.set(
        id,
        window.setTimeout(() => remove(id), DURATION),
      );
    },
    [remove],
  );

  useEffect(() => {
    const handles = timers.current;
    return () => {
      for (const h of handles.values()) window.clearTimeout(h);
      handles.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onClose={() => remove(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onClose }: { toast: ToastItem; onClose: () => void }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(r);
  }, []);
  const visible = shown && !toast.leaving;

  return (
    <div
      role={toast.tone === "error" ? "alert" : "status"}
      className={cn(
        "pointer-events-auto relative overflow-hidden rounded-md border border-border bg-surface py-2 pl-4 pr-8",
        "shadow-[0_10px_30px_-12px_var(--p-shadow-lg)]",
        "transition-all duration-200 ease-out motion-reduce:transition-none motion-reduce:translate-x-0",
        visible ? "translate-x-0 opacity-100" : "translate-x-3 opacity-0",
      )}
    >
      <span className={cn("absolute inset-y-0 left-0 w-1", STRIPE[toast.tone])} />
      <div className="text-[13px] font-semibold text-ink">{toast.title}</div>
      {toast.description && (
        <div className="mt-0.5 text-[12px] text-ink-muted">{toast.description}</div>
      )}
      <Button
        variant="ghost"
        size="xs"
        iconOnly
        aria-label="Yopish"
        onClick={onClose}
        className="absolute right-1.5 top-1.5 text-ink-faint"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast ToastProvider ichida ishlatilishi kerak");
  return ctx;
}
